import type { Account, Intent, Target } from './types';

export type AuthScreen =
    | 'contact'
    | 'register'
    | 'set-password'
    | 'join'
    | 'dashboard';

export function screenAfterAuthentication(
    account: Account | null,
    target: Target | null,
    registrationStage: 'phone' | 'profile' | null,
): AuthScreen {
    if (!account) {
        return registrationStage === 'profile' ? 'register' : 'contact';
    }

    // A client moved in from another club system has no password; nothing else until they
    // set one (owner, 2026-10-05). `false` only — an older server sends no flag at all.
    if (account.profile?.has_password === false) {
        return 'set-password';
    }

    return target && !hasJoinedTarget(account, target) ? 'join' : 'dashboard';
}

export function hasJoinedTarget(
    account: Account | null,
    target: Target | null,
): boolean {
    if (!account || !target) {
        return false;
    }

    return account.networks.some((network) =>
        network.clubs.some((club) => club.id === target.club.id),
    );
}

export function initials(first?: string | null, last?: string | null): string {
    return (
        `${first?.trim().charAt(0) ?? ''}${last?.trim().charAt(0) ?? ''}`.toUpperCase() ||
        'U'
    );
}

export function money(value: number, locale = 'uk'): string {
    const locales: Record<string, string> = {
        uk: 'uk-UA',
        ru: 'ru-RU',
        en: 'en-US',
    };

    return new Intl.NumberFormat(locales[locale] ?? locales.uk, {
        maximumFractionDigits: 2,
    }).format(value);
}

/**
 * Which sheets a deep-link intent opens, once its owner is on the dashboard.
 *
 * `password_reset` opens both. The password form is the thing the Shell's QR promised, so
 * it goes on top; the profile editor sits behind it so that closing the form lands
 * somewhere useful instead of on a bare dashboard, with no hint of where the password
 * lives. `profile` opens only the editor.
 */
export function sheetsForIntent(intent: Intent | null): {
    profile: boolean;
    password: boolean;
} {
    return {
        profile: intent !== null,
        password: intent === 'password_reset',
    };
}

/**
 * What a deep-link intent becomes once the player has just *created* their account.
 *
 * Registration asks for a password on its own form, so reopening «change your password»
 * seconds later is the same question twice; the editor is where the link promised to land
 * anyway. **Claiming an existing account is the opposite case** and must not call this:
 * that account already has a password, and having forgotten it is why the code was
 * scanned in the first place.
 */
export function intentAfterRegistration(intent: Intent | null): Intent | null {
    return intent === 'password_reset' ? 'profile' : intent;
}

/**
 * What the app says about an invitation opened by somebody who already has an account
 * (Черняк, 2026-10-07). It used to be a toast on the way into «Приєднатися до …», gone before
 * anyone read it, so a player joined and believed the friend who invited them was credited.
 *
 * - `join` — on the join screen, a standing line above the buttons;
 * - `joined` — after joining, once, on the dashboard;
 * - `member` — already in that club, so there is no join screen: once, on the dashboard.
 */
export type ReferralNote = 'join' | 'joined' | 'member' | 'existing';

export const referralNoteText: Record<ReferralNote, string> = {
    join: 'Запрошення діє лише для нових гравців, а у вас уже є акаунт.',
    joined: 'Ви приєдналися до клубу. Бонус за запрошення не нараховується: ви вже мали акаунт.',
    member: 'Ви вже в цьому клубі. Запрошення діє лише для нових гравців.',
    // No join screen and not a member: the invitation named no club, or a password comes first.
    existing: 'Запрошення діє лише для нових гравців, а у вас уже є акаунт.',
};

/**
 * The note for this launch. «You are already in this club» only when the server said so; a
 * club still to join keeps the note for its join screen, even when a password screen comes
 * first; anything else says the plain sentence on the dashboard (review, 2026-10-07).
 */
export function referralNoteOnLaunch(
    referralIgnored: boolean,
    alreadyJoined: boolean,
    hasTarget: boolean,
): ReferralNote | null {
    if (!referralIgnored) {
        return null;
    }

    if (alreadyJoined) {
        return 'member';
    }

    return hasTarget ? 'join' : 'existing';
}

/** After «Приєднатися»: the join-screen note becomes the confirmation; nothing else does. */
export function referralNoteAfterJoin(
    note: ReferralNote | null,
): ReferralNote | null {
    return note === 'join' ? 'joined' : null;
}

/**
 * The error screen in the player's language, and with a way out that works (Черняк,
 * 2026-10-07). The server's sentence came in Ukrainian under an English heading. And «Try
 * again» re-sent the same Telegram init data, which had already expired, so the retry could
 * never succeed.
 *
 * Known codes get the app's own words. `close` means only reopening the Mini App helps: Telegram
 * hands out fresh init data on a new launch. Anything unknown keeps the server's message and
 * the retry.
 */
export type ErrorScreen = { message: string | null; action: 'retry' | 'close' };

export const AUTH_EXPIRED_TEXT =
    'Термін дії авторизації Telegram минув. Закрийте та відкрийте Mini App знову.';
export const AUTH_INVALID_TEXT =
    'Не вдалося перевірити вхід через Telegram. Закрийте та відкрийте Mini App знову.';
export const OFFLINE_TEXT =
    "Немає з'єднання з сервером. Перевірте інтернет і спробуйте ще раз.";

export function errorScreenFor(caught: unknown): ErrorScreen {
    const code =
        typeof caught === 'object' && caught !== null && 'code' in caught
            ? (caught as { code: unknown }).code
            : null;

    if (code === 'init_data_expired') {
        return { message: AUTH_EXPIRED_TEXT, action: 'close' };
    }

    if (code === 'init_data_invalid') {
        return { message: AUTH_INVALID_TEXT, action: 'close' };
    }

    // `fetch` rejects with a TypeError when the request never reached the server.
    if (caught instanceof TypeError) {
        return { message: OFFLINE_TEXT, action: 'retry' };
    }

    return { message: null, action: 'retry' };
}
